<script setup>
// THE price box (X62, Grant 2026-10-19: money is always two decimals, inputs
// included). Bound to MINOR units; empty = free (null) as everywhere in the
// admin (no "charge a fee" tick), unless `zeroIsPrice` (the booking table,
// where "" copies the row above and 0 is free).
//
// While the box has focus the text is the user's own ("6", "6.5", "£6.50",
// "6,50" all parse as they type, so dirty tracking and estimates follow at
// once); on blur or Enter, and whenever a value lands from outside (a saved
// price loading, a preset picked), it is rewritten as "6.50" / "6.00".
import {computed, ref, watch} from 'vue';
import {useMeetAdmin} from '~/composables/useMeetAdmin';
import {moneyBoxLoaded, moneyBoxSettled, moneyBoxTyped, minorToMajor} from '~/utils/memberships';

const props = defineProps({
  currency: {type: String, default: 'gbp'},
  readonly: {type: Boolean, default: false},
  errorMessages: {type: [String, Array], default: null},
  hint: {type: String, default: null},
  placeholder: {type: String, default: '0.00'},
  // No default on purpose: the kit's VTextField default (compact) applies, so the box is the same height as the
  // fields beside it and the currency prefix sits on the input's baseline. 'default' here made every price box
  // 56px tall next to 46px neighbours with the placeholder out of line (Grant 2026-11-03, the setup assistant's Entry fee).
  density: {type: String, default: undefined},
  hideDetails: {type: [Boolean, String], default: 'auto'},
  // "0" is a real price (shown "0.00"); without it 0 reads as free and the box empties.
  zeroIsPrice: {type: Boolean, default: false},
});
const model = defineModel({type: [Number, null], default: null});
const {currencySymbol} = useMeetAdmin();

const opts = computed(() => ({zeroIsPrice: props.zeroIsPrice}));
const text = ref(minorToMajor(model.value, opts.value));
const focused = ref(false);

// A value from outside (load, preset, reset) lands as "6.00"; our own echo (the
// parent writing back what we just emitted) is left alone while typing.
watch(model, (v) => {
  text.value = moneyBoxLoaded(text.value, v, {focused: focused.value, ...opts.value});
});

const onInput = (value) => {
  const typed = moneyBoxTyped(value);
  text.value = typed.text;
  if (typed.minor !== model.value) model.value = typed.minor;
};
const settle = () => { text.value = moneyBoxSettled(text.value, opts.value); };
const onFocus = () => { focused.value = true; };
const onBlur = () => { focused.value = false; settle(); };

const prefix = computed(() => currencySymbol(props.currency));
</script>

<template>
  <v-text-field
      :model-value="text"
      type="text"
      inputmode="decimal"
      autocomplete="off"
      :prefix="prefix"
      :placeholder="placeholder"
      :readonly="readonly"
      :density="density"
      :hide-details="hideDetails"
      :error="!!errorMessages && errorMessages.length > 0"
      :error-messages="errorMessages || undefined"
      :hint="hint || undefined"
      :persistent-hint="!!hint"
      @update:model-value="onInput"
      @focus="onFocus"
      @blur="onBlur"
      @keydown.enter="settle"
  />
</template>
